'use client';

import Image from 'next/image';
import Link from 'next/link';
import React, { useCallback, useEffect, useState } from 'react';

import VideoLink from '@/components/video/VideoLink';

import { CinemaPromotionDto } from '@cineamo/api/models/cinema-promotion/CinemaPromotionDto.types';

import { isYoutubeVideoUrl } from '@/constants/youtube-constants';

import { cn } from '@cineamo/ui/src/helpers/cn';

import useHorizontalSwipeRecognizer from '@/hooks/useHorizontalSwipeRecognizer';

import ButtonWithIcon, { ButtonWithIconStyle } from '@cineamo/ui/src/button/ButtonWithIcon';
import { IconBack24 } from '@cineamo/ui/src/icons/ic-back';
import { IconForth24 } from '@cineamo/ui/src/icons/ic-forth';
import { IconPlayButton96 } from '@cineamo/ui/src/icons/ic-playButton';

type CustomBannerSliderProps = {
    banners: CinemaPromotionDto[];
};

export default function CustomBannerSlider({ banners }: CustomBannerSliderProps): React.JSX.Element | null {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, [banners.length]);

    const goToPrev = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
    }, [banners.length]);

    const { ref: swipeRef } = useHorizontalSwipeRecognizer({
        onLeftSwipe: goToNext,
        onRightSwipe: goToPrev
    });

    // Auto-advance every 6 seconds, pause on hover and while the video player modal is open
    // (advancing would unmount the current banner's VideoLink and close the modal)
    useEffect(() => {
        if (banners.length <= 1 || isHovered || isVideoModalOpen) return;

        const interval = setInterval(goToNext, 6000);
        return () => clearInterval(interval);
    }, [banners.length, goToNext, isHovered, isVideoModalOpen]);

    if (banners.length === 0) return null;

    const safeIndex = currentIndex < banners.length ? currentIndex : 0;
    const currentBanner = banners[safeIndex];
    const desktopImage = currentBanner.desktopImageUrl || currentBanner.mobileImageUrl;
    const mobileImage = currentBanner.mobileImageUrl || currentBanner.desktopImageUrl;

    const bannerHref = currentBanner.link ?? currentBanner.videoLink;
    const isSafeHref = (() => {
        if (!bannerHref) return false;
        // A root-relative path is an in-site link (the deals page's top-deals); `//` would be protocol-relative
        if (bannerHref.startsWith('/') && !bannerHref.startsWith('//')) return true;
        try {
            const parsed = new URL(bannerHref);
            return parsed.protocol === 'https:' || parsed.protocol === 'http:';
        } catch {
            return false;
        }
    })();
    // A YouTube url is treated as a video: the banner opens the player modal instead of navigating
    const isVideoBanner = isSafeHref && isYoutubeVideoUrl(bannerHref);

    const bannerInner = (
        <div
            ref={swipeRef as React.RefObject<HTMLDivElement>}
            className="relative w-full"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            {/* Banner image */}
            <div className="relative w-full overflow-hidden rounded-8">
                {/* Desktop image */}
                <div className="relative w-full aspect-[1920/500] hidden lg:block">
                    {desktopImage ? (
                        <Image
                            src={desktopImage}
                            alt={currentBanner.imageDescription || ''}
                            fill
                            // A 16:9 image (a deal's) in this 1920/500 frame keeps its upper part, where the headline sits
                            className="object-cover object-[center_25%]"
                            priority={safeIndex === 0}
                        />
                    ) : (
                        <div className="w-full h-full bg-darkBlue/50" />
                    )}
                </div>
                {/* Mobile image */}
                <div className="relative w-full aspect-[1920/900] lg:hidden">
                    {mobileImage ? (
                        <Image
                            src={mobileImage}
                            alt={currentBanner.imageDescription || ''}
                            fill
                            className="object-cover"
                            priority={safeIndex === 0}
                        />
                    ) : (
                        <div className="w-full h-full bg-darkBlue/50" />
                    )}
                </div>

                {/* Subtle edge gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20 rounded-8" />

                {/* Play button overlay for video banners; the click on the surrounding VideoLink opens the player */}
                {isVideoBanner && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <IconPlayButton96
                            foregroundcolor="white"
                            fill="black"
                        />
                    </div>
                )}

                {/* Dot indicators */}
                {banners.length > 1 && (
                    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-12 z-10">
                        {banners.map((_, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCurrentIndex(index);
                                }}
                                className={cn(
                                    'rounded-full transition-all duration-300 size-12',
                                    index === safeIndex ? 'bg-[var(--primary-color)]' : 'bg-white/50 hover:bg-white/70'
                                )}
                                aria-label={`Go to banner ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Outside arrows — desktop only, hidden on mobile (swipe instead) */}
            {banners.length > 1 && (
                <div className="hidden md:flex absolute top-0 w-full h-full pointer-events-none items-center justify-between">
                    <ButtonWithIcon
                        className="pointer-events-auto -translate-x-48"
                        icon={<IconBack24 />}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goToPrev();
                        }}
                        style={ButtonWithIconStyle.transparent}
                        accessibilityName="Previous banner"
                    />
                    <ButtonWithIcon
                        className="pointer-events-auto translate-x-48 ml-auto"
                        icon={<IconForth24 />}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goToNext();
                        }}
                        style={ButtonWithIconStyle.transparent}
                        accessibilityName="Next banner"
                    />
                </div>
            )}
        </div>
    );

    if (isVideoBanner) {
        return (
            <VideoLink
                url={bannerHref}
                trackingEvent="Banner Video Played"
                onModalOpenChange={setIsVideoModalOpen}
                className="block cursor-pointer">
                {bannerInner}
            </VideoLink>
        );
    }

    if (bannerHref && isSafeHref) {
        return (
            <Link
                href={bannerHref}
                target={currentBanner.videoLink && !currentBanner.link ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="block">
                {bannerInner}
            </Link>
        );
    }

    return bannerInner;
}
